import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "~/app/store/baseApi";
import { occBaseSiteUrl, occFetch } from "~/app/api/occ/occFetch";
import type { OccCartUserPath } from "~/app/api/occ/occUserPath";

export interface MinicartResponse {
  miniCartCount: number;
  priceValue?: number;
  currencyIso?: string;
}

export interface MinicartQueryArg {
  activeCartId: string;
  projectionVersion: number;
  userPath: OccCartUserPath;
}

/**
 * RTK Query wrapper for the explicit Active Cart minicart projection.
 *
 * This is the sole minicart data source for React (ADR 0003). Cart mutations invalidate the {@code Minicart}
 * tag to trigger a refetch; the Minicart tag is never invalidated by identity changes (Session tag).
 */
export const minicartApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMinicart: builder.query<MinicartResponse, MinicartQueryArg>({
      queryFn: async ({ activeCartId, userPath }) => {
        const response = await fetchExplicitMinicart(activeCartId, userPath);
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }

        const data = (await response.json()) as MinicartResponse;
        return { data };
      },
      providesTags: ["Minicart"],
    }),
  }),
});

export const { useGetMinicartQuery } = minicartApi;

export function fetchExplicitMinicart(activeCartId: string, userPath: OccCartUserPath): Promise<Response> {
  if (!activeCartId) {
    return Promise.reject(new Error("Cannot load minicart without an explicit Active Cart identity"));
  }
  return occFetch(occBaseSiteUrl(`${userPath}/carts/${encodeURIComponent(activeCartId)}/miniCart`));
}
